import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Config, Data, Layout, PlotlyHTMLElement } from 'plotly.js';
import type { ChartImage, PlotAnnotation, PlotShape, PlotSpec, PlotTrace } from '../types/chartTypes';

type PlotlyApi = Pick<typeof import('plotly.js'), 'Plots' | 'react' | 'purge' | 'register' | 'toImage'>;

type LivePlotSizeValue = number | string;

export type LivePlotSizeOptions = {
  fit?: 'width' | 'height';
  width?: LivePlotSizeValue;
  height?: LivePlotSizeValue;
  minHeight?: LivePlotSizeValue;
  maxWidth?: LivePlotSizeValue;
  maxHeight?: LivePlotSizeValue;
  aspectRatio?: number | string;
  titleFontSize?: number;
  axisTitleFontSize?: number;
  tickFontSize?: number;
  legendFontSize?: number;
  plotMargin?: Partial<NonNullable<Layout['margin']>>;
  showTitle?: boolean;
  displayModeBar?: boolean;
  staticPlot?: boolean;
  legendPlacement?: 'auto' | 'external' | 'plot' | 'hidden';
};

type LivePlotProps = LivePlotSizeOptions & {
  plot?: PlotSpec;
  className?: string;
};

export type LivePlotImageOptions = {
  width?: number;
  height?: number;
  scale?: number;
  layout?: Partial<Layout>;
};

export type LivePlotHandle = {
  createImage: (options?: LivePlotImageOptions) => Promise<ChartImage>;
};

let plotlyPromise: Promise<PlotlyApi> | null = null;

const DEFAULT_TRACE_COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

function loadPlotly(): Promise<PlotlyApi> {
  // Plotly is large; load only the core package plus scatter traces when a result chart is actually shown.
  plotlyPromise ??= Promise.all([import('plotly.js/lib/core'), import('plotly.js/lib/scatter')]).then(([plotlyModule, scatterModule]) => {
    const Plotly = ((plotlyModule as { default?: unknown }).default ?? plotlyModule) as PlotlyApi;
    const Scatter = (scatterModule as { default?: unknown }).default ?? scatterModule;
    Plotly.register([Scatter] as never);
    return Plotly;
  });

  return plotlyPromise;
}

function traceColor(trace: PlotTrace, index: number): string | undefined {
  return trace.color ?? DEFAULT_TRACE_COLORS[index % DEFAULT_TRACE_COLORS.length];
}

function traceToData(trace: PlotTrace, index: number, showLegend: boolean): Data {
  const color = traceColor(trace, index);
  const data: Partial<Data> = {
    type: 'scatter',
    mode: trace.mode ?? 'lines',
    name: trace.name,
    x: trace.x,
    y: trace.y,
    showlegend: showLegend && (trace.showLegend ?? Boolean(trace.name)),
    line: {
      color,
      dash: trace.lineStyle ?? 'solid',
      width: trace.lineWidth
    }
  };

  if (trace.hoverTemplate) {
    data.hovertemplate = trace.hoverTemplate;
  }

  if (trace.marker) {
    data.marker = {
      color: trace.marker.color ?? color,
      size: trace.marker.size,
      symbol: trace.marker.symbol,
      line: {
        color: trace.marker.lineColor,
        width: trace.marker.lineWidth
      }
    };
  }

  return data as Data;
}

type PlotlyShape = NonNullable<Layout['shapes']>[number];
type PlotlyAnnotation = NonNullable<Layout['annotations']>[number];

function shapeLine(shape: PlotShape) {
  return {
    color: shape.color,
    dash: shape.lineStyle ?? 'solid',
    width: shape.lineWidth
  };
}

function shapeToLayoutShape(shape: PlotShape): PlotlyShape {
  if (shape.kind === 'vline') {
    return {
      type: 'line',
      xref: 'x',
      // Paper coordinates let full-height/full-width guide lines survive axis autorange changes.
      yref: shape.y0 === undefined && shape.y1 === undefined ? 'paper' : 'y',
      x0: shape.x,
      x1: shape.x,
      y0: shape.y0 ?? 0,
      y1: shape.y1 ?? 1,
      line: shapeLine(shape)
    } as PlotlyShape;
  }

  if (shape.kind === 'hline') {
    return {
      type: 'line',
      // Paper coordinates let full-height/full-width guide lines survive axis autorange changes.
      xref: shape.x0 === undefined && shape.x1 === undefined ? 'paper' : 'x',
      yref: 'y',
      x0: shape.x0 ?? 0,
      x1: shape.x1 ?? 1,
      y0: shape.y,
      y1: shape.y,
      line: shapeLine(shape)
    } as PlotlyShape;
  }

  return {
    type: 'line',
    xref: 'x',
    yref: 'y',
    x0: shape.x0,
    x1: shape.x1,
    y0: shape.y0,
    y1: shape.y1,
    line: shapeLine(shape)
  } as PlotlyShape;
}

function shapeToAnnotation(shape: PlotShape): PlotlyAnnotation | null {
  if (!shape.label) return null;

  if (shape.kind === 'vline') {
    return {
      x: shape.x,
      y: shape.y1 ?? 1,
      yref: shape.y1 === undefined ? 'paper' : 'y',
      text: shape.label,
      showarrow: false,
      xanchor: 'left',
      yanchor: 'bottom',
      font: { color: shape.color }
    } as PlotlyAnnotation;
  }

  if (shape.kind === 'hline') {
    return {
      x: shape.x1 ?? 1,
      xref: shape.x1 === undefined ? 'paper' : 'x',
      y: shape.y,
      text: shape.label,
      showarrow: false,
      xanchor: 'left',
      yanchor: 'bottom',
      font: { color: shape.color }
    } as PlotlyAnnotation;
  }

  return {
    x: shape.x1,
    y: shape.y1,
    text: shape.label,
    showarrow: false,
    xanchor: 'left',
    yanchor: 'bottom',
    font: { color: shape.color }
  } as PlotlyAnnotation;
}

function annotationToLayoutAnnotation(annotation: PlotAnnotation): PlotlyAnnotation {
  const showArrow = annotation.showArrow ?? false;

  return {
    x: annotation.x,
    y: annotation.y,
    text: annotation.text,
    showarrow: showArrow,
    ax: showArrow ? annotation.xShift : undefined,
    ay: showArrow ? annotation.yShift : undefined,
    xshift: showArrow ? undefined : annotation.xShift,
    yshift: showArrow ? undefined : annotation.yShift,
    textangle: annotation.angle,
    font: {
      color: annotation.color,
      size: annotation.fontSize
    }
  } as PlotlyAnnotation;
}

function escapePlotlyText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function wrapPlotTitle(title: string, maxLineLength = 54): string {
  const sourceLines = title
    .replace(/\s+/g, ' ')
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean);
  const wrappedLines: string[] = [];

  for (const sourceLine of sourceLines.length > 0 ? sourceLines : [title.trim()]) {
    let currentLine = '';

    for (const word of sourceLine.split(' ')) {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;
      if (nextLine.length > maxLineLength && currentLine) {
        wrappedLines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = nextLine;
      }
    }

    if (currentLine) {
      wrappedLines.push(currentLine);
    }
  }

  return wrappedLines.map(escapePlotlyText).join('<br>');
}

type PlotLayoutOptions = {
  titleFontSize?: number;
  axisTitleFontSize?: number;
  tickFontSize?: number;
  plotMargin?: Partial<NonNullable<Layout['margin']>>;
  showTitle?: boolean;
};

function plotToLayout(plot: PlotSpec, useExternalLegend: boolean, options: PlotLayoutOptions = {}): Partial<Layout> {
  const shapeAnnotations = plot.shapes?.map(shapeToAnnotation).filter((annotation): annotation is PlotlyAnnotation => annotation !== null) ?? [];
  const annotations = [...shapeAnnotations, ...(plot.annotations?.map(annotationToLayoutAnnotation) ?? [])];
  const visibleLegendCount = plot.traces.filter(trace => trace.showLegend ?? Boolean(trace.name)).length;
  const hasDenseLegend = visibleLegendCount > 8;
  const wrappedTitle = options.showTitle === false ? undefined : plot.title ? wrapPlotTitle(plot.title) : undefined;
  const titleLineCount = wrappedTitle ? wrappedTitle.split('<br>').length : 0;
  const titleFontSize = options.titleFontSize ?? (hasDenseLegend ? 13 : 16);
  const axisTitleFontSize = options.axisTitleFontSize;
  const tickFontSize = options.tickFontSize;
  const defaultMargin = {
    l: 56,
    r: 16,
    t: wrappedTitle ? Math.max(50, 28 + titleLineCount * Math.max(18, titleFontSize + 4)) : 22,
    b: useExternalLegend ? 54 : hasDenseLegend ? 116 : 76
  };

  return {
    autosize: true,
    title: wrappedTitle ? { text: wrappedTitle, font: { size: titleFontSize } } : undefined,
    margin: { ...defaultMargin, ...options.plotMargin },
    xaxis: {
      title: { text: plot.xLabel, font: axisTitleFontSize === undefined ? undefined : { size: axisTitleFontSize } },
      type: plot.xScale === 'log' ? 'log' : 'linear',
      range: plot.xRange,
      automargin: true,
      tickfont: tickFontSize === undefined ? undefined : { size: tickFontSize },
      showline: true,
      mirror: true,
      linecolor: 'rgba(15, 23, 42, 0.45)',
      linewidth: 1,
      gridcolor: 'rgba(15, 23, 42, 0.09)',
      zeroline: false,
      ticks: 'outside',
      ticklen: 4
    },
    yaxis: {
      title: { text: plot.yLabel, font: axisTitleFontSize === undefined ? undefined : { size: axisTitleFontSize } },
      type: plot.yScale === 'log' ? 'log' : 'linear',
      range: plot.yRange,
      automargin: true,
      tickfont: tickFontSize === undefined ? undefined : { size: tickFontSize },
      showline: true,
      mirror: true,
      linecolor: 'rgba(15, 23, 42, 0.45)',
      linewidth: 1,
      gridcolor: 'rgba(15, 23, 42, 0.09)',
      zeroline: false,
      ticks: 'outside',
      ticklen: 4
    },
    legend: {
      orientation: 'h',
      x: 0.5,
      y: hasDenseLegend ? -0.27 : -0.26,
      xanchor: 'center',
      yanchor: 'top',
      font: { size: hasDenseLegend ? 11 : 12 }
    },
    showlegend: !useExternalLegend && plot.traces.some(trace => trace.showLegend ?? Boolean(trace.name)),
    shapes: plot.shapes?.map(shapeToLayoutShape),
    annotations: annotations.length > 0 ? annotations : undefined,
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    hovermode: 'closest'
  };
}

function visibleLegendTraces(plot: PlotSpec) {
  return plot.traces.map((trace, index) => ({ trace, index, color: traceColor(trace, index) })).filter(({ trace }) => trace.showLegend ?? Boolean(trace.name));
}

function mergeLayout(base: Partial<Layout>, override: Partial<Layout>): Partial<Layout> {
  // Image export needs temporary layout overrides without dropping nested axis, margin, or legend defaults.
  return {
    ...base,
    ...override,
    margin: { ...base.margin, ...override.margin },
    font: { ...base.font, ...override.font },
    title: typeof base.title === 'object' || typeof override.title === 'object' ? { ...(typeof base.title === 'object' ? base.title : { text: base.title }), ...(typeof override.title === 'object' ? override.title : { text: override.title }) } : (override.title ?? base.title),
    xaxis: { ...base.xaxis, ...override.xaxis },
    yaxis: { ...base.yaxis, ...override.yaxis },
    legend: { ...base.legend, ...override.legend }
  };
}

function cssSize(value: LivePlotSizeValue | undefined): string | number | undefined {
  return typeof value === 'number' ? `${value}px` : value;
}

function plotSizeStyle(size: LivePlotSizeOptions, defaultHeight: string, defaultMinHeight: string): CSSProperties {
  if (size.aspectRatio !== undefined && size.fit === 'height') {
    return {
      width: cssSize(size.width ?? 'auto'),
      maxWidth: cssSize(size.maxWidth),
      height: cssSize(size.height ?? '100%'),
      maxHeight: cssSize(size.maxHeight),
      minHeight: cssSize(size.minHeight),
      aspectRatio: String(size.aspectRatio),
      flexShrink: 1
    };
  }

  return {
    width: cssSize(size.width ?? '100%'),
    maxWidth: cssSize(size.maxWidth),
    maxHeight: cssSize(size.maxHeight),
    height: cssSize(size.height ?? (size.aspectRatio === undefined ? defaultHeight : undefined)),
    minHeight: cssSize(size.minHeight ?? (size.aspectRatio === undefined ? defaultMinHeight : undefined)),
    aspectRatio: size.aspectRatio === undefined ? undefined : String(size.aspectRatio)
  };
}

function PlotStateMessage({ children }: { children: string }) {
  return <div className="flex h-80 min-h-72 w-full items-center justify-center rounded-md bg-white p-4 text-center text-sm italic text-slate-500">{children}</div>;
}

export const LivePlot = forwardRef<LivePlotHandle, LivePlotProps>(function LivePlot({ plot, className = '', fit, width, height, minHeight, maxWidth, maxHeight, aspectRatio, titleFontSize, axisTitleFontSize, tickFontSize, legendFontSize, plotMargin, showTitle = true, displayModeBar = true, staticPlot = false, legendPlacement = 'auto' }, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphDivRef = useRef<PlotlyHTMLElement | null>(null);
  const renderPromiseRef = useRef<Promise<void> | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(plot ? 'loading' : 'error');
  const visibleLegendCount = plot?.traces.filter(trace => trace.showLegend ?? Boolean(trace.name)).length ?? 0;
  const hasDenseLegend = visibleLegendCount > 8;
  const useExternalLegend = legendPlacement === 'external' || (legendPlacement === 'auto' && hasDenseLegend);
  const showPlotLegend = legendPlacement !== 'hidden';
  const sizeStyle = useMemo(() => plotSizeStyle({ fit, width, height, minHeight, maxWidth, maxHeight, aspectRatio }, hasDenseLegend ? '28rem' : '20rem', hasDenseLegend ? '28rem' : '18rem'), [aspectRatio, fit, hasDenseLegend, height, maxHeight, maxWidth, minHeight, width]);
  const data = useMemo(() => plot?.traces.map((trace, index) => traceToData(trace, index, showPlotLegend && !useExternalLegend)) ?? [], [plot, showPlotLegend, useExternalLegend]);
  const legendTraces = useMemo(() => (plot && showPlotLegend && useExternalLegend ? visibleLegendTraces(plot) : []), [plot, showPlotLegend, useExternalLegend]);
  const layout = useMemo(() => (plot ? plotToLayout(plot, useExternalLegend, { titleFontSize, axisTitleFontSize, tickFontSize, plotMargin, showTitle }) : null), [axisTitleFontSize, plot, plotMargin, showTitle, tickFontSize, titleFontSize, useExternalLegend]);
  const config = useMemo<Partial<Config>>(
    () => ({
      displayModeBar,
      modeBarButtonsToRemove: ['autoScale2d', 'lasso2d', 'select2d', 'zoomIn2d', 'zoomOut2d'],
      modeBarButtonsToAdd: [],
      displaylogo: false,
      responsive: true,
      scrollZoom: !staticPlot,
      staticPlot
    }),
    [displayModeBar, staticPlot]
  );

  useEffect(() => {
    if (!plot || !layout) {
      return undefined;
    }

    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    const plotLayout = layout;

    async function renderPlot() {
      try {
        const Plotly = await loadPlotly();
        if (cancelled || !containerRef.current) return;

        const graphDiv = containerRef.current as unknown as PlotlyHTMLElement;
        graphDivRef.current = graphDiv;
        await Plotly.react(graphDiv, data, plotLayout, config);
        // Plotly sometimes measures before CSS grid/flex has settled; one resize on the next frame fixes clipped modebars and axes.
        requestAnimationFrame(() => {
          if (!cancelled) Plotly.Plots.resize(graphDiv);
        });

        // Keep charts responsive without rerendering React state on every container resize.
        resizeObserver = new ResizeObserver(() => {
          if (!cancelled) Plotly.Plots.resize(graphDiv);
        });
        resizeObserver.observe(graphDiv);

        if (!cancelled) setStatus('ready');
      } catch (error) {
        console.error('Failed to render live plot:', error);
        if (!cancelled) setStatus('error');
      }
    }

    const renderPromise = renderPlot();
    renderPromiseRef.current = renderPromise;
    void renderPromise;

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
    };
  }, [config, data, layout, plot]);

  useEffect(() => {
    return () => {
      if (!graphDivRef.current) return;
      void loadPlotly().then(Plotly => {
        if (graphDivRef.current) Plotly.purge(graphDivRef.current);
      });
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      async createImage(options = {}) {
        await renderPromiseRef.current;

        if (!graphDivRef.current || !layout) {
          throw new Error('Live chart is not ready for image export.');
        }

        const Plotly = await loadPlotly();
        const graphDiv = graphDivRef.current;

        try {
          if (options.layout) {
            // Print/export images need larger fonts and fixed dimensions, but the live chart should keep its screen layout.
            await Plotly.react(graphDiv, data, mergeLayout(layout, options.layout), config);
          }

          const dataUrl = await Plotly.toImage(graphDiv, { format: 'png', width: options.width ?? null, height: options.height ?? null, scale: options.scale ?? 2 });
          const [, base64 = ''] = dataUrl.split(',');

          if (!base64) {
            throw new Error('Plotly image export returned an empty image.');
          }

          return { mime: 'image/png', base64 };
        } finally {
          if (options.layout) {
            // Restore the interactive chart after snapshotting so hover, zoom, and modebar behavior stay unchanged.
            await Plotly.react(graphDiv, data, layout, config);
          }
        }
      }
    }),
    [config, data, layout]
  );

  if (!plot) return <PlotStateMessage>Live chart data is not available for this result.</PlotStateMessage>;
  if (status === 'error') return <PlotStateMessage>Unable to render live chart.</PlotStateMessage>;

  return (
    <div className={`relative flex flex-col ${className}`} style={sizeStyle}>
      {status === 'loading' ? <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center text-sm italic text-slate-500">Loading live chart...</div> : null}
      <div ref={containerRef} className="relative z-10 min-h-0 w-full flex-1" />
      {useExternalLegend && legendTraces.length > 0 ? (
        <div className="grid shrink-0 grid-cols-[repeat(auto-fit,minmax(3.2rem,1fr))] gap-x-2 gap-y-0.5 px-2 pb-0.5 pt-0.5 leading-none text-slate-700 sm:px-8" style={{ fontSize: legendFontSize === undefined ? undefined : `${legendFontSize}px` }}>
          {legendTraces.map(({ trace, index, color }) => (
            <div key={`${trace.name ?? 'trace'}-${index}`} className="flex min-w-0 items-center justify-center gap-1.5">
              <span className="w-5 shrink-0 rounded-full border-t-2" style={{ borderColor: color }} />
              <span className="truncate">{trace.name}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
});
